<template>
  <div class="projects-page">

    <!-- Sidebar -->
    <div class="sidebar">
      <div class="sidebar-top">
        <span class="sidebar-heading">Projects</span>
        <button
          v-if="!atProjectLimit"
          class="new-btn"
          :class="{ disabled: createNewMode }"
          @click="enableCreateNewMode"
          title="New project"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
            <path d="M8 2v12M2 8h12"/>
          </svg>
        </button>
      </div>

      <!-- Project limit notice -->
      <div v-if="atProjectLimit" class="limit-notice">
        <svg class="limit-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
          <circle cx="8" cy="8" r="6.5"/>
          <path d="M8 5v3.5M8 11h.01"/>
        </svg>
        <div class="limit-text">
          <span>{{ subscriptionProduct?.name ?? 'Free' }} plan: {{ subscriptionProduct?.maxProjects ?? 2 }} projects</span>
          <router-link :to="{ name: 'settings' }" class="upgrade-link">Upgrade →</router-link>
        </div>
      </div>

      <!-- Projects list -->
      <div class="projects-list">
        <div
          v-for="project in projectsOrdered"
          :key="project.id"
          class="project-item"
          :class="{ active: selectedProjectId === project.id && !createNewMode }"
          @click="onSelectProject(project)"
        >
          <div class="project-dot" :style="{ background: project.color || 'var(--primary)' }"></div>
          <span class="project-item-name">{{ project.title }}</span>
        </div>

        <!-- New project inline indicator -->
        <div v-if="createNewMode" class="project-item active new-item">
          <div class="project-dot new-dot"></div>
          <span class="project-item-name">New project</span>
        </div>
      </div>

      <!-- Invitations -->
      <div v-if="myInvitations.length > 0 || myInvitationsLoading" class="invitations-section">
        <div class="invitations-label">Invitations</div>
        <div v-if="myInvitationsLoading" class="members-loading">
          <Loading size="16"></Loading>
        </div>
        <template v-else>
          <div v-for="inv in myInvitations" :key="inv.id" class="invitation-item">
            <div class="invitation-info">
              <span class="invitation-project">{{ inv.projectTitle }}</span>
              <span class="invitation-meta">{{ inv.roleName === 'projectOwner' ? 'Owner' : 'Contributor' }} · by {{ inv.invitedByEmail }}</span>
            </div>
            <div class="invitation-actions">
              <Button
                variant="primary smooth"
                class="small"
                :loading="invitationActionLoading === inv.id"
                :disabled="invitationActionLoading === inv.id"
                @click="acceptInvitation(inv)"
              >Accept</Button>
              <Button
                variant="smooth"
                class="small"
                :disabled="invitationActionLoading === inv.id"
                @click="rejectInvitation(inv)"
              >Decline</Button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Main content -->
    <div class="main-content">

      <!-- Empty state -->
      <div v-if="!selectedProject && !createNewMode" class="empty-state">
        <div class="empty-icon-wrap">
          <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="6" y="10" width="36" height="28" rx="4"/>
            <path d="M6 18h36"/>
            <path d="M16 10V6M32 10V6"/>
            <path d="M15 27h18M15 33h10"/>
          </svg>
        </div>
        <div class="empty-title">No project selected</div>
        <div class="empty-sub">Pick a project from the sidebar to view its settings and members.</div>
        <Button v-if="!atProjectLimit" @click="enableCreateNewMode" variant="primary smooth" class="mt-5">New project</Button>
        <router-link v-else :to="{ name: 'settings' }" class="upgrade-btn-empty">Upgrade to add more projects</router-link>
      </div>

      <!-- Project detail -->
      <template v-else>

        <!-- Color banner -->
        <div class="project-banner" :style="bannerStyle">
          <div class="banner-overlay"></div>
          <div class="banner-content">
            <div class="banner-title">{{ createNewMode ? 'New project' : titleInput }}</div>
            <div v-if="!createNewMode" class="banner-sub">{{ members.length }} member{{ members.length !== 1 ? 's' : '' }}</div>
          </div>
        </div>

        <div class="detail-body">

          <!-- Settings card -->
          <div class="detail-section">
            <div class="section-label">Settings</div>
            <div class="settings-card">
              <Input
                label="Title"
                ref="titleInput"
                :value="titleInput"
                @input="titleInput = $event"
                class="mb-4"
              ></Input>
              <ColorPicker
                label="Color"
                :value="colorInput"
                @input="colorInput = $event"
                class="mb-4"
              ></ColorPicker>
              <Input
                label="Default earning per hour"
                type="number"
                :value="defaultEarningPerHour"
                @input="defaultEarningPerHour = $event"
                class="mb-4"
              ></Input>
              <p v-if="saveError" class="save-error">{{ saveError }}</p>
              <div class="form-actions">
                <Button v-if="!createNewMode && hasChanges" variant="text" :disabled="saveLoading" @click="undoChanges">Undo changes</Button>
                <div class="xs12"></div>
                <Button variant="primary smooth" :loading="saveLoading" :disabled="saveLoading" @click="saveProject">
                  {{ createNewMode ? 'Create project' : 'Save changes' }}
                </Button>
              </div>
            </div>
          </div>

          <!-- Members section (only when editing existing project) -->
          <div v-if="selectedProjectId && !createNewMode" class="detail-section">
            <div class="section-label-row">
              <div class="section-label">Members</div>
              <div v-if="isFree" class="member-count-badge" :class="{ warn: atMemberLimit }">
                {{ nonSelfMemberCount }}/2
              </div>
            </div>

            <div v-if="membersLoading" class="members-loading">
              <Loading size="18"></Loading>
            </div>

            <template v-else>
              <div class="members-list">
                <!-- Active members -->
                <div v-for="member in members" :key="member.userId" class="member-row">
                  <div class="member-avatar" :style="avatarStyle(member.email)">
                    {{ member.email[0].toUpperCase() }}
                  </div>
                  <div class="member-info">
                    <span class="member-email">{{ member.email }}</span>
                    <span class="member-you" v-if="member.userId === currentUserId">you</span>
                  </div>
                  <select
                    class="role-select"
                    :value="member.roleName"
                    :disabled="member.userId === currentUserId || isLastOwner(member)"
                    @change="updateMemberRole(member, $event.target.value)"
                  >
                    <option value="projectOwner">Owner</option>
                    <option value="projectContributor">Contributor</option>
                  </select>
                  <button
                    class="member-remove-btn"
                    :disabled="isLastOwner(member)"
                    v-tooltip="isLastOwner(member) ? 'Cannot remove the last owner' : member.userId === currentUserId ? 'Leave project' : 'Remove member'"
                    @click="removeMember(member)"
                  >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <path d="M3 3l10 10M13 3L3 13"/>
                    </svg>
                  </button>
                </div>

                <!-- Pending invitations -->
                <template v-if="projectInvitations.length > 0">
                  <div class="pending-header">Pending</div>
                  <div v-for="inv in projectInvitations" :key="inv.id" class="member-row pending-row">
                    <div class="member-avatar pending-avatar">?</div>
                    <div class="member-info">
                      <span class="member-email pending-email">{{ inv.email }}</span>
                      <span class="role-chip">{{ inv.roleName === 'projectOwner' ? 'Owner' : 'Contributor' }}</span>
                    </div>
                    <span class="pending-label">Invited</span>
                    <button class="member-remove-btn" v-tooltip="'Cancel invitation'" @click="cancelInvitation(inv)">
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <path d="M3 3l10 10M13 3L3 13"/>
                      </svg>
                    </button>
                  </div>
                </template>
              </div>

              <!-- Member limit notice -->
              <div v-if="atMemberLimit" class="member-limit-notice">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
                  <circle cx="8" cy="8" r="6.5"/>
                  <path d="M8 5v3.5M8 11h.01"/>
                </svg>
                <span>Free plan allows 2 members per project.</span>
                <router-link :to="{ name: 'settings' }" class="upgrade-link">Upgrade →</router-link>
              </div>

              <!-- Invite form -->
              <div v-if="!atMemberLimit" class="invite-form">
                <input
                  class="invite-input"
                  type="email"
                  v-model="addEmail"
                  placeholder="Email address"
                  @keydown.enter="addMember"
                />
                <select class="role-select" v-model="addRole">
                  <option value="projectContributor">Contributor</option>
                  <option value="projectOwner">Owner</option>
                </select>
                <Button
                  variant="primary smooth"
                  class="small"
                  :loading="addLoading"
                  :disabled="!addEmail || addLoading"
                  @click="addMember"
                >Invite</Button>
              </div>

              <p v-if="addError" class="add-error">{{ addError }}</p>
            </template>
          </div>

          <!-- Danger zone (only when editing an existing project) -->
          <div v-if="selectedProjectId && !createNewMode" class="detail-section">
            <div class="section-label">Danger zone</div>
            <div class="danger-card">
              <template v-if="!confirmDelete">
                <div class="danger-info">
                  <span class="danger-title">Delete this project</span>
                  <span class="danger-sub">Its boards, tasks, comments and time entries are deleted too.</span>
                </div>
                <Button variant="smooth" class="danger-btn" @click="confirmDelete = true">Delete project</Button>
              </template>
              <template v-else>
                <div class="danger-info">
                  <span class="danger-title">Delete &ldquo;{{ selectedProject?.title }}&rdquo;?</span>
                  <span class="danger-sub">This cannot be undone.</span>
                </div>
                <div class="danger-actions">
                  <Button variant="text" :disabled="deleteLoading" @click="confirmDelete = false">Cancel</Button>
                  <Button
                    variant="smooth"
                    class="danger-btn"
                    :loading="deleteLoading"
                    :disabled="deleteLoading"
                    @click="deleteProject"
                  >Yes, delete</Button>
                </div>
              </template>
            </div>
            <p v-if="deleteError" class="add-error">{{ deleteError }}</p>
          </div>

        </div>
      </template>
    </div>

  </div>
</template>

<script>
  import Input from '../common/Input.vue'
  import Button from '../common/Button.vue'
  import ColorPicker from '../common/ColorPicker.vue'
  import Loading from '../common/Loading.vue'
  import { api } from '@/utils'

  const AVATAR_COLORS = [
    ['#dbeafe', '#1d4ed8'],
    ['#dcfce7', '#15803d'],
    ['#fef3c7', '#b45309'],
    ['#fce7f3', '#be185d'],
    ['#ede9fe', '#6d28d9'],
    ['#ffedd5', '#c2410c'],
  ]

  export default {
    name: 'Projects',
    components: { Input, Button, ColorPicker, Loading },
    data: () => ({
      selectedProjectId: null,
      titleInput: '',
      colorInput: '',
      defaultEarningPerHour: null,
      saveLoading: false,
      saveError: null,
      confirmDelete: false,
      deleteLoading: false,
      deleteError: null,
      createNewMode: false,
      members: [],
      membersLoading: false,
      projectInvitations: [],
      addEmail: '',
      addRole: 'projectContributor',
      addLoading: false,
      addError: null,
      myInvitations: [],
      myInvitationsLoading: false,
      invitationActionLoading: null,
    }),
    computed: {
      selectedProject() {
        return this.projectsOrdered.find((p) => p.id === this.selectedProjectId)
      },
      projectsOrdered() {
        return this.$store.getters.projectsOrdered
      },
      currentUserId() {
        return this.$store.getters.user?.id
      },
      subscription() {
        return this.$store.getters.subscription || {}
      },
      subscriptionProduct() {
        return this.$store.getters.subscriptionProduct
      },
      isFree() {
        return this.subscriptionProduct?.isFree ?? true
      },
      ownedProjectCount() {
        return this.projectsOrdered.filter(p => p.userId === this.currentUserId).length
      },
      atProjectLimit() {
        const max = this.subscriptionProduct?.maxProjects ?? null
        return max !== null && this.ownedProjectCount >= max
      },
      nonSelfMemberCount() {
        return this.members.filter(m => m.userId !== this.currentUserId).length
      },
      atMemberLimit() {
        const max = this.subscriptionProduct?.maxMembers ?? null
        if (max === null) return false
        const pendingCount = this.projectInvitations.length
        return (this.nonSelfMemberCount + pendingCount) >= max
      },
      hasChanges() {
        if (this.createNewMode || !this.selectedProject) return false
        return this.titleInput !== this.selectedProject.title ||
          this.colorInput !== this.selectedProject.color ||
          this.defaultEarningPerHour != this.selectedProject.defaultEarningPerHour
      },
      bannerStyle() {
        const color = this.colorInput || '#3D6A90'
        return { background: color }
      },
    },
    watch: {
      selectedProjectId(id) {
        const p = this.selectedProject
        if (id && p) {
          this.titleInput = p.title
          this.colorInput = p.color
          this.defaultEarningPerHour = p.defaultEarningPerHour
          this.loadMembers()
        } else {
          this.titleInput = ''
          this.colorInput = ''
          this.defaultEarningPerHour = null
          this.members = []
          this.projectInvitations = []
        }
      }
    },
    mounted() {
      this.loadMyInvitations()
    },
    methods: {
      avatarStyle(email) {
        const idx = email.charCodeAt(0) % AVATAR_COLORS.length
        const [bg, color] = AVATAR_COLORS[idx]
        return { background: bg, color }
      },
      onSelectProject(project) {
        this.createNewMode = false
        this.addError = null
        this.saveError = null
        this.resetDeleteState()
        this.selectedProjectId = project.id
        this.titleInput = project.title
        this.colorInput = project.color
        this.defaultEarningPerHour = project.defaultEarningPerHour
      },
      enableCreateNewMode() {
        if (this.createNewMode) return
        this.saveError = null
        this.resetDeleteState()
        this.selectedProjectId = null
        this.createNewMode = true
        this.titleInput = ''
        this.colorInput = ''
        this.defaultEarningPerHour = null
        this.$nextTick(() => {
          this.$refs.titleInput?.focus()
        })
      },
      undoChanges() {
        this.titleInput = this.selectedProject.title
        this.colorInput = this.selectedProject.color
        this.defaultEarningPerHour = this.selectedProject.defaultEarningPerHour
      },
      cancelEdit() {
        if (this.selectedProjectId) {
          if (
            this.titleInput === this.selectedProject.title &&
            this.colorInput === this.selectedProject.color
          ) {
            this.selectedProjectId = null
            this.titleInput = ''
            this.colorInput = ''
            this.defaultEarningPerHour = null
          } else {
            this.titleInput = this.selectedProject.title
            this.colorInput = this.selectedProject.color
            this.defaultEarningPerHour = this.selectedProject.defaultEarningPerHour
          }
        } else {
          if (this.titleInput || this.colorInput) {
            this.titleInput = ''
            this.colorInput = ''
            this.defaultEarningPerHour = null
          } else {
            this.createNewMode = false
          }
        }
      },
      async saveProject() {
        this.saveLoading = true
        this.saveError = null
        const action = this.createNewMode ? 'createItems' : 'updateItems'
        try {
          await this.$store.dispatch(action, {
            projects: [{
              id: this.selectedProjectId,
              title: this.titleInput,
              color: this.colorInput,
              defaultEarningPerHour: this.defaultEarningPerHour ? parseFloat(this.defaultEarningPerHour) : null
            }]
          })
          if (this.createNewMode) {
            this.cancelEdit()
            this.cancelEdit()
          }
        } catch (e) {
          this.saveError = e?.message || 'Could not save the project'
        } finally {
          this.saveLoading = false
        }
      },
      resetDeleteState() {
        this.confirmDelete = false
        this.deleteError = null
      },
      async deleteProject() {
        const projectId = this.selectedProjectId
        this.deleteLoading = true
        this.deleteError = null
        try {
          await this.$store.dispatch('deleteProject', projectId)
          // Drop the selection before handing control back, so no render can
          // reference the project the store has just removed.
          this.resetDeleteState()
          this.selectedProjectId = null
        } catch (e) {
          this.deleteError = e?.message || 'Could not delete the project'
        } finally {
          this.deleteLoading = false
        }
      },
      isLastOwner(member) {
        return member.roleName === 'projectOwner' &&
          this.members.filter((m) => m.roleName === 'projectOwner').length <= 1
      },
      async loadMembers() {
        if (!this.selectedProjectId) return
        this.membersLoading = true
        try {
          const [membersRes, invitationsRes] = await Promise.all([
            api({ url: `project-tracker/projects/${this.selectedProjectId}/members`, method: 'get' }),
            api({ url: `project-tracker/projects/${this.selectedProjectId}/invitations`, method: 'get' })
          ])
          this.members = membersRes.data
          this.projectInvitations = invitationsRes.data
        } finally {
          this.membersLoading = false
        }
      },
      async loadMyInvitations() {
        this.myInvitationsLoading = true
        try {
          this.myInvitations = (await api({ url: 'project-tracker/invitations', method: 'get' })).data
        } finally {
          this.myInvitationsLoading = false
        }
      },
      async addMember() {
        if (!this.addEmail) return
        this.addLoading = true
        this.addError = null
        try {
          const invitation = (await api({
            url: `project-tracker/projects/${this.selectedProjectId}/members`,
            method: 'post',
            body: { email: this.addEmail, role: this.addRole }
          })).data
          this.projectInvitations.push(invitation)
          this.addEmail = ''
          this.addRole = 'projectContributor'
        } catch (e) {
          this.addError = e?.response?.data?.message || e?.message || 'Could not send invitation'
        } finally {
          this.addLoading = false
        }
      },
      async updateMemberRole(member, newRole) {
        try {
          const updated = (await api({
            url: `project-tracker/projects/${this.selectedProjectId}/members/${member.userId}`,
            method: 'put',
            body: { role: newRole }
          })).data
          member.roleName = updated.roleName
        } catch (e) {
          await this.loadMembers()
        }
      },
      async removeMember(member) {
        try {
          await api({
            url: `project-tracker/projects/${this.selectedProjectId}/members/${member.userId}`,
            method: 'delete'
          })
          if (member.userId === this.currentUserId) {
            await Promise.all([
              this.$store.dispatch('getUser'),
              this.$store.dispatch('getProjects')
            ])
            this.selectedProjectId = null
          } else {
            this.members = this.members.filter((m) => m.userId !== member.userId)
          }
        } catch (e) {
          this.addError = e?.response?.data?.message || e?.message || 'Could not remove member'
        }
      },
      async cancelInvitation(inv) {
        try {
          await api({
            url: `project-tracker/projects/${this.selectedProjectId}/invitations/${inv.id}`,
            method: 'delete'
          })
          this.projectInvitations = this.projectInvitations.filter((i) => i.id !== inv.id)
        } catch (e) {
          this.addError = e?.response?.data?.message || e?.message || 'Could not cancel invitation'
        }
      },
      async acceptInvitation(inv) {
        this.invitationActionLoading = inv.id
        try {
          await api({ url: `project-tracker/invitations/${inv.id}/accept`, method: 'post' })
          this.myInvitations = this.myInvitations.filter((i) => i.id !== inv.id)
          await Promise.all([
            this.$store.dispatch('getUser'),
            this.$store.dispatch('getProjects')
          ])
        } catch (e) {
          // keep invitation visible on error
        } finally {
          this.invitationActionLoading = null
        }
      },
      async rejectInvitation(inv) {
        this.invitationActionLoading = inv.id
        try {
          await api({ url: `project-tracker/invitations/${inv.id}/reject`, method: 'post' })
          this.myInvitations = this.myInvitations.filter((i) => i.id !== inv.id)
        } catch (e) {
          // keep invitation visible on error
        } finally {
          this.invitationActionLoading = null
        }
      }
    }
  }
</script>

<style scoped>
/* ─── Page layout ─────────────────────────────────────── */
.projects-page {
  display: flex;
  height: 100%;
  overflow: hidden;
}

/* ─── Sidebar ─────────────────────────────────────────── */
.sidebar {
  width: 240px;
  flex-shrink: 0;
  border-right: 1px solid var(--app-border);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 20px 0;
  background: rgba(0, 0, 0, 0.015);
}

.sidebar-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 12px;
}

.sidebar-heading {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--app-text-muted);
}

.new-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  padding: 0;
}
.new-btn svg { width: 14px; height: 14px; }
.new-btn:hover { background: var(--app-bg-hover); color: var(--app-text); }
.new-btn.disabled { opacity: 0.35; cursor: default; pointer-events: none; }

/* Project limit notice */
.limit-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 8px 8px;
  padding: 10px 12px;
  background: rgba(180, 83, 9, 0.07);
  border: 1px solid rgba(180, 83, 9, 0.15);
  border-radius: 8px;
}
.limit-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  margin-top: 1px;
  color: #b45309;
}
.limit-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 0.75rem;
  color: #92400e;
  line-height: 1.3;
}
.upgrade-link {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
}
.upgrade-link:hover { text-decoration: underline; }

/* Projects list */
.projects-list {
  flex: 1;
  padding: 0 8px;
}

.project-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
  margin-bottom: 2px;
}
.project-item:hover { background: var(--app-bg-hover); }
.project-item.active { background: var(--primary-muted); }

.project-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.new-dot {
  background: var(--primary);
  opacity: 0.4;
}

.project-item-name {
  font-size: 0.875rem;
  color: var(--app-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 450;
}
.project-item.active .project-item-name { color: var(--primary); font-weight: 600; }
.new-item .project-item-name { color: var(--app-text-muted); font-style: italic; }

/* Invitations */
.invitations-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--app-border);
  padding: 16px 12px 0;
}
.invitations-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--app-text-muted);
  margin-bottom: 8px;
  padding: 0 4px;
}
.invitation-item {
  padding: 10px 6px;
  border-bottom: 1px solid var(--app-border);
}
.invitation-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}
.invitation-project {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--app-text);
}
.invitation-meta {
  font-size: 0.73rem;
  color: var(--app-text-muted);
}
.invitation-actions { display: flex; gap: 6px; }

/* ─── Main content ────────────────────────────────────── */
.main-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Empty state */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
}
.empty-icon-wrap {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: var(--primary-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}
.empty-icon-wrap svg {
  width: 36px;
  height: 36px;
  color: var(--primary);
  opacity: 0.7;
}
.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text);
  margin-bottom: 6px;
}
.empty-sub {
  font-size: 0.85rem;
  color: var(--app-text-muted);
  max-width: 280px;
  line-height: 1.5;
}
.upgrade-btn-empty {
  margin-top: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  padding: 8px 16px;
  border: 1.5px solid var(--primary);
  border-radius: 8px;
  transition: background 0.12s;
}
.upgrade-btn-empty:hover { background: var(--primary-muted); }

/* Banner */
.project-banner {
  height: 88px;
  position: relative;
  flex-shrink: 0;
  transition: background 0.3s ease;
}
.banner-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.05) 100%);
}
.banner-content {
  position: relative;
  z-index: 1;
  padding: 20px 28px;
}
.banner-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0,0,0,0.2);
  letter-spacing: -0.01em;
}
.banner-sub {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.75);
  margin-top: 2px;
}

/* Detail body */
.detail-body {
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  flex: 1;
}

/* Sections */
.detail-section { display: flex; flex-direction: column; gap: 10px; }

.section-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--app-text-muted);
}
.section-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.member-count-badge {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  background: var(--app-bg-hover);
  color: var(--app-text-muted);
  letter-spacing: 0.03em;
}
.member-count-badge.warn {
  background: rgba(180, 83, 9, 0.1);
  color: #b45309;
}

/* Settings card */
.settings-card {
  background: var(--app-bg-card);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  padding: 20px;
  max-width: 360px;
}
.form-actions {
  display: flex;
  align-items: center;
  padding-top: 4px;
}

/* Members */
.members-loading {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}
.members-list {
  background: var(--app-bg-card);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  overflow: hidden;
  max-width: 520px;
}

.member-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--app-border);
  transition: background 0.1s;
}
.member-row:last-child { border-bottom: none; }
.member-row:hover { background: rgba(0,0,0,0.02); }

.pending-header {
  padding: 7px 14px 4px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--app-text-muted);
  background: rgba(0,0,0,0.02);
  border-top: 1px solid var(--app-border);
}

.member-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pending-avatar {
  background: rgba(0,0,0,0.06);
  color: rgba(0,0,0,0.3);
}
.pending-email { color: var(--app-text-muted) !important; }

.member-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.member-email {
  font-size: 0.875rem;
  color: var(--app-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.member-you {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--app-text-muted);
  background: var(--app-bg-hover);
  border-radius: 4px;
  padding: 1px 5px;
  flex-shrink: 0;
}
.role-chip {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--app-text-muted);
  background: var(--app-bg-hover);
  border-radius: 4px;
  padding: 1px 5px;
  flex-shrink: 0;
}
.pending-label {
  font-size: 0.72rem;
  color: var(--app-text-muted);
  flex-shrink: 0;
}

.role-select {
  height: 28px;
  border: 1px solid var(--app-border);
  border-radius: 6px;
  background: var(--app-bg-input);
  padding: 0 6px;
  font-size: 0.78rem;
  color: var(--app-text);
  outline: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.12s;
}
.role-select:focus { border-color: rgba(61, 106, 144, 0.4); }
.role-select:disabled { opacity: 0.4; cursor: not-allowed; }

.member-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(0,0,0,0.2);
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
  padding: 0;
}
.member-remove-btn svg { width: 11px; height: 11px; }
.member-remove-btn:hover:not(:disabled) { color: #b03a3a; background: rgba(190,60,60,0.08); }
.member-remove-btn:disabled { opacity: 0.25; cursor: not-allowed; }

/* Member limit notice */
.member-limit-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(180, 83, 9, 0.07);
  border: 1px solid rgba(180, 83, 9, 0.15);
  border-radius: 8px;
  font-size: 0.8rem;
  color: #92400e;
  max-width: 520px;
}
.member-limit-notice svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #b45309;
}
.member-limit-notice .upgrade-link { margin-left: auto; }

/* Invite form */
.invite-form {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 520px;
}
.invite-input {
  flex: 1;
  height: 34px;
  border: 1px solid var(--app-border);
  border-radius: 8px;
  background: var(--app-bg-card);
  padding: 0 12px;
  font-size: 0.875rem;
  color: var(--app-text);
  outline: none;
  min-width: 0;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.invite-input:focus {
  border-color: rgba(61,106,144,0.5);
  box-shadow: 0 0 0 3px var(--primary-muted);
}
.invite-input::placeholder { color: var(--app-text-muted); }

.danger-card {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px 16px;
  max-width: 520px;
  background: var(--app-bg-card);
  border: 1px solid rgba(176, 58, 58, 0.28);
  border-radius: 12px;
}
.danger-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 220px;
  min-width: 0;
}
.danger-title { font-size: 0.85rem; font-weight: 600; }
.danger-sub { font-size: 0.75rem; color: var(--app-text-muted); }
.danger-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.danger-btn {
  color: #b03a3a;
  border-color: rgba(176, 58, 58, 0.35);
}

.add-error,
.save-error {
  font-size: 0.78rem;
  color: #b03a3a;
  margin: 4px 0 0;
}
</style>
